import { BackTop, Table, Card, Row, Col, Statistic } from "antd";
import React, { useState, useEffect } from "react";
import PrescriptionsFilterBar from "../../../components/filter-bars/prescriptions-report-filters";
import { cedisLocale, exportToExcel } from "../../../helpers/utilities";

export default function PrescriptionsReport({ data, isLoading }) {
  const [filteredData, setFilteredData] = useState([]);

  const handleReset = () => {
    setFilteredData(data);
  };

  const handleApplyFilter = (newData) => {
    setFilteredData(newData);
  };

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  // Calculate metrics for the current filtered data
  const calculateMetrics = () => {
    const totalPrescriptions = filteredData?.length;
    const issuedPrescriptions = filteredData?.filter(
      (prescription) => prescription?.isIssued
    ).length;
    const notIssuedPrescriptions = totalPrescriptions - issuedPrescriptions;
    const totalRevenue = filteredData?.reduce((sum, prescription) => {
      return (
        sum +
        (prescription?.isIssued ? parseFloat(prescription?.price) || 0 : 0)
      );
    }, 0);
    const issuanceRate =
      totalPrescriptions > 0
        ? (issuedPrescriptions / totalPrescriptions) * 100
        : 0;

    // Count unique drugs
    const uniqueDrugs = [
      ...new Set(filteredData?.map((prescription) => prescription?.medicine)),
    ].length;

    // Count unique pharmacies
    const uniquePharmacies = [
      ...new Set(
        filteredData?.map(
          (prescription) => prescription?.pharmacy?.pharmacyName
        )
      ),
    ].filter(Boolean).length;

    return {
      totalPrescriptions,
      issuedPrescriptions,
      notIssuedPrescriptions,
      totalRevenue,
      issuanceRate,
      uniqueDrugs,
      uniquePharmacies,
    };
  };

  const columns = [
    {
      title: "#",
      // sorter: ( a, b ) => a.insuranceNumber > b.insuranceNumber,
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) => ++index,
    },
    {
      title: "Date - Time",
      sorter: (a, b) => a?.createdAt > b?.createdAt,
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) =>
        new Date(record?.createdAt).toUTCString(),
    },
    {
      title: "Patient",
      sorter: (a, b) =>
        a?.consultation?.patient?.firstName >
        b?.consultation?.patient?.firstName,
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) =>
        `${record?.consultation.patient.firstName}  ${record?.consultation?.patient?.lastName}`,
    },
    // {
    //   title: "Insurance Provider",
    //   sorter: (a, b) => a.gender > b.gender,
    //   sortDirections: ["descend", "ascend"],
    //   render: (text, record, index) =>
    //     record?.consultation?.patient?.insuranceProvider?.providerName,
    // },
    // {
    //   title: "Insurance #",
    //   dataIndex: ["consultation", "patient", "insuranceNumber"],
    //   sorter: (a, b) =>
    //     a?.consultation?.patient?.insuranceNumber >
    //     b?.consultation?.patient?.insuranceNumber,
    //   sortDirections: ["descend", "ascend"],
    // },
    {
      title: "Doctor",
      sorter: (a, b) =>
        a?.consultation?.doctor?.firstName > b?.consultation?.doctor?.firstName,
      sortDirections: ["descend", "ascend"],
      render: (t, r, i) => {
        return r?.consultation?.doctor?.firstName
          ? `${r?.consultation?.doctor?.firstName}  ${r?.consultation?.doctor?.lastName}`
          : "n/a";
      },
    },
    {
      title: "Drug",
      sorter: (a, b) => a?.medicine > b?.medicine,
      dataIndex: "medicine",
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Dosage",
      sorter: (a, b) => a?.dosage > b?.dosage,
      dataIndex: "dosage",
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Status",
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) =>
        record?.isIssued ? "Issued" : "Not Issued",
    },
    {
      title: "Price",
      sorter: (a, b) => a.price > b.price,
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) =>
        record?.isIssued && cedisLocale.format(record.price),
    },
    {
      title: "Pharmacy",
      sorter: (a, b) => a?.pharmacy?.pharmacyName > b?.pharmacy?.pharmacyName,
      dataIndex: ["pharmacy", "pharmacyName"],
      sortDirections: ["descend", "ascend"],
    },
    // {
    //     title: "Issuer",
    //     sorter: ( a, b ) => a.totalConsultations > b.totalConsultations,
    //     dataIndex: "totalConsultations",
    //     sortDirections: [ "descend", "ascend" ],
    // },
    {
      title: "Date Issued",
      sorter: (a, b) => a?.updatedAt > b?.updatedAt,
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) =>
        record?.isIssued && new Date(record?.updatedAt).toUTCString(),
    },
  ];

  const metrics = calculateMetrics();

  return (
    <div>
      <PrescriptionsFilterBar
        data={data}
        onReset={handleReset}
        onFilter={(d) => handleApplyFilter(d)}
        excelExport={() =>
          exportToExcel(
            "prescriptionsReportTable",
            "rivia-prescriptions-report"
          )
        }
      />

      {/* Metrics Cards */}
      <Row gutter={16} className="mt-3 mb-3">
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Total Prescriptions"
              value={metrics.totalPrescriptions}
              valueStyle={{ color: "#3f8600" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Issued"
              value={metrics.issuedPrescriptions}
              valueStyle={{ color: "#3f8600" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Not Issued"
              value={metrics.notIssuedPrescriptions}
              valueStyle={{ color: "#faad14" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Total Revenue"
              value={cedisLocale.format(metrics.totalRevenue)}
              valueStyle={{ color: "#3f8600" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Unique Drugs"
              value={metrics.uniqueDrugs}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Issuance Rate"
              value={metrics.issuanceRate.toFixed(1)}
              suffix="%"
              valueStyle={{
                color:
                  metrics.issuanceRate >= 80
                    ? "#3f8600"
                    : metrics.issuanceRate >= 60
                    ? "#faad14"
                    : "#cf1322",
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* advanced filters
            
            -> date range
            -> doctor
            -> status
            -> age
            
            */}
      <Table
        className="border mt-3"
        id="prescriptionsReportTable"
        rowKey={(record) => record.id}
        dataSource={filteredData}
        columns={columns}
        loading={isLoading}
        bordered
        tableLayout="auto"
      />
      <BackTop />
    </div>
  );
}
